import { Component, DestroyRef, EventEmitter, Input, OnDestroy, Output, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Constants } from '../../utils/constants';
import { Project } from '../../models/project';
import { ModelsToBeRun } from '../../models/models-to-be-run';
import { SelectedProjectApiService } from '../selected-project/selected-project.api.service';
import { TabMenuLogicService } from '../../services/tab-menu.logic.service';
import { EEAEndpointsByCompartmentLogicService } from 'src/app/environmental-exposure-assessment/eea-endpoints/eea-endpoints-by-compartment/eea-endpoints-by-compartment.logic.service';

@Component({
  selector: 'app-model-checkbox',
  templateUrl: './model-checkbox.component.html',
  styleUrls: ['./model-checkbox.component.css']
})
export class ModelCheckboxComponent implements OnDestroy {

  @Input() selectedProject?: Project;
  @Input() compartment: any;
  @Input() metabolitePk?: number;
  @Input() datasetPk?: number;
  @Input() datasetMetabolitePk?: number;
  @Input() selectModels: boolean = true;
  @Input() menuService?: TabMenuLogicService;
  @Input() subTabMenuService?: TabMenuLogicService;
  options: string[] = [];
  selectedOptions: string[] = [];
  disabledOptions: string[] = [];
  @Output() onModelOptionsChangedEvent = new EventEmitter<any>();
  @Input() isProjectOwnershipValid: boolean = false;

  private modelsToBeRunRecords: ModelsToBeRun[] = [];
  private destroyRef = inject(DestroyRef);
  private isExcludedFromAI: boolean = false;
  private baseDisabledOptions: string[] = [];

  constructor(private readonly selectedProjectApiService: SelectedProjectApiService,
    private readonly endpointByCompartmentLogicService: EEAEndpointsByCompartmentLogicService) {
  }

  ngOnInit(): void {
    this.initSaveSubscriptions();
    this.initExcludeMetaboliteSubscription();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['metabolitePk'] && !changes['metabolitePk'].isFirstChange()) {
      const previousMetabolitePk = changes['metabolitePk'].previousValue;
      if (previousMetabolitePk !== changes['metabolitePk'].currentValue) {
        this.saveModelsSelection(previousMetabolitePk);
      }
    }

    if (changes['selectedProject']?.currentValue?.projectPk || changes['datasetPk'] || changes['metabolitePk'] || changes['datasetMetabolitePk'] || changes['compartment']) {
      this.loadModels();
    }
  }

  ngOnDestroy(): void {
    this.saveModelsSelection(this.metabolitePk);
  }

  onModelOptionsChanged(event: string[]) {
    this.selectedOptions = event;
    this.onModelOptionsChangedEvent.emit(event);
  }

  private initSaveSubscriptions(): void {
    this.menuService?.activeIndexChanged
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.saveModelsSelection(this.metabolitePk));
    this.subTabMenuService?.activeIndexChanged
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.saveModelsSelection(this.metabolitePk));
  }

  private initExcludeMetaboliteSubscription(): void {
    this.endpointByCompartmentLogicService.excludeMetabolite$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.isExcludedFromAI = value === true;
        this.selectedOptions = value === true ? [] : this.selectedOptions;
        this.updateDisabledOptions();
      });
  }

  private updateDisabledOptions(): void {
    this.disabledOptions = this.isExcludedFromAI ? [...this.options] : [...this.baseDisabledOptions];
  }

  private loadModels(): void {
    if (!this.selectedProject?.projectPk || !this.datasetPk) return;

    this.selectedProjectApiService.getModelsToBeRun(this.selectedProject.projectPk, this.datasetPk, this.datasetMetabolitePk)
      .subscribe(records => this.processModelsToBeRun(records));
  }

  private processModelsToBeRun(records: ModelsToBeRun[]): void {
    this.modelsToBeRunRecords = records;

    const forCurrentMetabolite = records.filter(r =>
      this.metabolitePk ? r.MetabolitePk === this.metabolitePk : !r.MetabolitePk
    );

    const modelsByCompartment = Constants.SPECIFIC_MODELS_BY_COMPARTMENT.find(x => x.compartment === this.compartment)?.models ?? [];
    const availableModelNames = forCurrentMetabolite.map(r => r.ModelName!).filter(Boolean);
    this.options = modelsByCompartment.filter(model => availableModelNames.includes(model));

    const isViewingDatasetOwnMetabolite = this.metabolitePk != null && this.metabolitePk === this.datasetMetabolitePk;

    if (this.metabolitePk && !isViewingDatasetOwnMetabolite) {
      const activeParentModels = records
        .filter(r => (this.datasetMetabolitePk ? r.MetabolitePk === this.datasetMetabolitePk : !r.MetabolitePk) && r.RunInProject !== false)
        .map(r => r.ModelName!);
      this.baseDisabledOptions = this.options.filter(o => !activeParentModels.includes(o));
    } else {
      this.baseDisabledOptions = [];
    }

    this.updateDisabledOptions();

    if (this.selectModels) {
      this.selectedOptions = this.options.filter(model => {
        if (this.baseDisabledOptions.includes(model)) return false;
        const record = forCurrentMetabolite.find(r => r.ModelName === model);
        return record ? record.RunInProject !== false : true;
      });
    }

    this.onModelOptionsChangedEvent.emit(this.selectedOptions);
  }

  private saveModelsSelection(metabolitePk: number | undefined): void {
    if (!this.datasetPk || !this.selectedProject?.projectPk || this.options.length === 0) return;

    const updates = this.modelsToBeRunRecords
      .filter(r =>
        this.options.includes(r.ModelName!) &&
        (metabolitePk ? r.MetabolitePk === metabolitePk : !r.MetabolitePk)
      )
      .map(r => {
        const newValue = this.selectedOptions.includes(r.ModelName!);
        if (r.RunInProject === newValue) return null;
        return { ModelsToBeRunPk: r.ModelsToBeRunPk!, RunInProject: newValue };
      })
      .filter((u): u is { ModelsToBeRunPk: number; RunInProject: boolean } => u !== null);

    if (updates.length === 0) return;

    updates.forEach(u => {
      const record = this.modelsToBeRunRecords.find(r => r.ModelsToBeRunPk === u.ModelsToBeRunPk);
      if (record) record.RunInProject = u.RunInProject;
    });

    this.selectedProjectApiService.updateModelsToBeRun(updates).subscribe({
      error: (error: Error) => console.error(error)
    });
  }
}

